import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { History, RefreshCw, Search, ChevronDown } from "lucide-react";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AuditLogCard() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit-log", { params: { q: query, limit: 200 } });
      setRows(data);
    } catch { /* admin-only card — silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(q); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="glass overflow-hidden mb-6" data-testid="audit-log-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full p-5 flex items-center gap-3 text-left hover:bg-white/40 transition-colors"
        data-testid="audit-log-toggle"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,58,237,0.12)" }}>
          <History size={16} style={{ color: "#7C3AED" }} />
        </div>
        <div className="flex-1">
          <div className="kpi-label">Full audit trail</div>
          <h3 className="font-display text-xl font-semibold tracking-tight">Activity log — who did what, when</h3>
        </div>
        <ChevronDown size={18} className="text-ink-3 transition-transform" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </button>

      {open && (
        <div className="border-t border-black/5">
          <div className="p-4 flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load(q)}
                placeholder="Filter by email, action or path — press Enter"
                className="w-full text-xs pl-8 pr-3 py-2.5 rounded-xl outline-none"
                style={{ background: "rgba(15,18,38,0.04)", border: "1px solid rgba(15,18,38,0.07)" }}
                data-testid="audit-log-search"
              />
            </div>
            <button onClick={() => load(q)} disabled={loading} className="btn-secondary !py-2 !px-3 text-xs" data-testid="audit-log-refresh">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 420, overflowY: "auto" }}>
            <table className="modern-table w-full crm-compact">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Detail</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-8 text-ink-3 text-sm" data-testid="audit-log-empty">{loading ? "Loading…" : "No activity recorded yet."}</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                    <td className="num text-xs text-ink-3 whitespace-nowrap">{fmtTime(r.at)}</td>
                    <td className="text-xs">
                      <span className="font-medium">{r.actor_email}</span>
                      {r.impersonated_by && (
                        <span className="chip text-[9px] ml-1.5" style={{ background: "rgba(217,119,6,0.12)", color: "#B45309" }} title={`Action performed by ${r.impersonated_by} while viewing as this user`}>
                          via {r.impersonated_by}
                        </span>
                      )}
                    </td>
                    <td className="text-xs font-medium">{r.action}</td>
                    <td className="font-mono text-[10px] text-ink-3">{r.method} {r.path}</td>
                    <td>
                      <span
                        className="chip text-[10px]"
                        style={r.status < 400
                          ? { background: "rgba(0,134,58,0.10)", color: "#00863A" }
                          : { background: "rgba(220,38,38,0.10)", color: "#B91C1C" }}
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
